import React, { useState, useEffect, useRef } from "react";
import myAvatar from "../assets/my-avatar.png";
import { PERSONAL_INFO, SOCIAL_LINKS } from "../data/socials";
import {
  IoMailOutline,
  IoDocumentTextOutline,
  IoLocationOutline,
  IoLogoGithub,
  IoChevronDownOutline,
  IoChevronUpOutline,
} from "react-icons/io5";

export default function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let timeoutId;
    const handleScroll = () => {
      el.classList.add("is-scrolling");
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        el.classList.remove("is-scrolling");
      }, 1000);
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <aside
      ref={scrollRef}
      className="glass-panel p-6 flex flex-col items-center text-center glow-purple rounded-3xl lg:h-full w-full lg:overflow-y-auto hide-scrollbar-inactive"
    >
      {/* Mobile Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="absolute top-4 right-4 lg:hidden p-2 rounded-lg bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 transition-colors text-zinc-400 hover:text-white"
        aria-label="Toggle contact details"
      >
        {isOpen ? <IoChevronUpOutline className="text-lg" /> : <IoChevronDownOutline className="text-lg" />}
      </button>

      {/* Profile Header */}
      <div className="flex flex-col items-center">
        <div className="relative group">
          <div className="absolute -inset-1 rounded-2xl bg-gradient-to-tr from-violet-500 to-cyan-500 opacity-30 group-hover:opacity-60 blur transition duration-500"></div>
          <figure className="relative w-24 h-24 rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 p-1">
            <img
              src={myAvatar}
              alt={PERSONAL_INFO.name}
              className="w-full h-full object-cover rounded-xl"
              width="96"
            />
          </figure>
        </div>

        <h1 className="text-2xl font-semibold font-display tracking-tight text-white mt-4">
          {PERSONAL_INFO.name}
        </h1>

        <p className="text-xs font-medium text-violet-400 bg-violet-500/10 border border-violet-500/20 px-3 py-1 rounded-full mt-2">
          {PERSONAL_INFO.role}
        </p>

        <div className="flex items-center gap-2 mt-3.5 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-[11px] font-medium text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
          {PERSONAL_INFO.status}
        </div>
      </div>

      {/* Collapsible Info Section */}
      <div className={`w-full transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100 mt-6' : 'max-h-0 opacity-0 lg:max-h-[500px] lg:opacity-100 lg:mt-6 overflow-hidden'}`}>
        <div className="w-full h-[1px] bg-zinc-800/60 mb-6"></div>

        <ul className="flex flex-col gap-4 w-full">
          {/* Email */}
          <li className="flex items-center gap-3 w-full text-left group">
            <div className="w-9 h-9 rounded-lg bg-zinc-900/50 border border-zinc-800 flex items-center justify-center text-zinc-450 group-hover:text-violet-400 group-hover:border-violet-500/30 transition-colors">
              <IoMailOutline className="text-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-wider">Email</p>
              <a
                href={`mailto:${PERSONAL_INFO.email}`}
                className="text-sm text-zinc-300 hover:text-white transition-colors truncate block font-sans"
              >
                {PERSONAL_INFO.email}
              </a>
            </div>
          </li>

          {/* Github */}
          <li className="flex items-center gap-3 w-full text-left group">
            <div className="w-9 h-9 rounded-lg bg-zinc-900/50 border border-zinc-800 flex items-center justify-center text-zinc-450 group-hover:text-violet-400 group-hover:border-violet-500/30 transition-colors">
              <IoLogoGithub className="text-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-wider">Github</p>
              <a
                href={SOCIAL_LINKS.find((s) => s.id === "github")?.url || "https://github.com/adiabhiraj195"}
                className="text-sm text-zinc-300 hover:text-white transition-colors truncate block font-sans"
                target="_blank"
                rel="noreferrer"
              >
                github.com/{SOCIAL_LINKS.find((s) => s.id === "github")?.handle || "adiabhiraj195"}
              </a>
            </div>
          </li>

          {/* Location */}
          <li className="flex items-center gap-3 w-full text-left group">
            <div className="w-9 h-9 rounded-lg bg-zinc-900/50 border border-zinc-800 flex items-center justify-center text-zinc-450 group-hover:text-violet-400 group-hover:border-violet-500/30 transition-colors">
              <IoLocationOutline className="text-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-wider">Location</p>
              <address className="text-sm text-zinc-350 not-italic font-sans">
                {PERSONAL_INFO.location}
              </address>
            </div>
          </li>

          {/* Resume */}
          <li className="flex items-center gap-3 w-full text-left group">
            <div className="w-9 h-9 rounded-lg bg-zinc-900/50 border border-zinc-800 flex items-center justify-center text-zinc-450 group-hover:text-violet-400 group-hover:border-violet-500/30 transition-colors">
              <IoDocumentTextOutline className="text-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-wider">Resume</p>
              <a
                href={PERSONAL_INFO.resumeUrl}
                className="text-sm text-zinc-300 hover:text-white transition-colors truncate block font-sans"
                target="_blank"
                rel="noreferrer"
              >
                Download Resume
              </a>
            </div>
          </li>
        </ul>

        <div className="w-full h-[1px] bg-zinc-800/60 my-6"></div>

        {/* Social Links */}
        <ul className="flex items-center justify-center gap-4">
          {SOCIAL_LINKS.filter((s) => s.id !== "email").map((s) => {
            const SocialIcon = s.icon;
            return (
              <li key={s.id}>
                <a
                  href={s.url}
                  className="w-8 h-8 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-700 transition-all hover:-translate-y-0.5"
                  target="_blank"
                  rel="noreferrer"
                  title={s.name}
                >
                  <SocialIcon className="text-base" />
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
}

